/**
 * Lead Information (S1/S2/S3) constants.
 *
 * Field lists used by `LeadInformationComponent` to:
 * - emit Action Required form values
 * - validate required fields per stage
 *
 * NOTE: Keep these in sync with the form control names in `createLeadInformationForm()`.
 */

import { ELeadStageTitle } from '@app/core/enum/client.enum'

/**
 * IST (Indian Standard Time) formatters for converting UTC datetime to local display formats
 */
export const IST_DATE_TIME_FORMATTER = new Intl.DateTimeFormat('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
})

export const IST_DATE_FORMATTER = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Kolkata',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
})

export const IST_TIME_FORMATTER = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Kolkata',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
})

export const S1_FORM_VALUE_FIELDS: readonly string[] = [
    'principalName',
    'schoolEmail',
    'primaryBoard',
]

export const S2_FORM_VALUE_FIELDS: readonly string[] = [
    'principalKdmMeetingConfirmed',
    'kdmNameS2',
    'kdmEmailS2',
    'kdmMobileS2',
    'kdmDesignationS2',
    'totalStudents',
    'hasExistingEdTechVendors',
    'demoDate',
    'demoTime',
    'demoDateFinalized',
    'annualFee',
    'tuitionFee',
]

export const S3_FORM_VALUE_FIELDS: readonly string[] = [
    'demoParticipantIds',
    's3Notes',
    'demoStatus',
]

export const MANDATORY_S1_FIELDS: readonly string[] = [
    'primaryBoard',
    'schoolAddress',
    'schoolEmail',
    'principalName',
    'principalPhone',
]

export const S1_BOARD_AMOUNT_RESTORE_MESSAGE =
    'Please enter an amount or restore the previous value.'

export const S1_BOARD_STRENGTH_RESTORE_MESSAGE =
    'Please enter student strength or restore the previous value.'

export const S1_BOARD_REQUIRES_GRADES_MESSAGE = 'Please select at least one grade for this board.'

/** S2 structured vendor details (`vendorDetails` form array). Used by `vendor-detail-validators`. */
export const LEAD_VENDOR_NAME_MIN_LENGTH = 3
export const LEAD_VENDOR_NAME_MAX_LENGTH = 100
export const LEAD_VENDOR_PRODUCTS_MAX_COUNT = 10

/** Letters, numbers, spaces, and only &, comma, period, hyphen (no other special chars). */
export const LEAD_VENDOR_NAME_PATTERN_STRING = '^[a-zA-Z0-9\\s&,\\.\\-]+$'

export const LEAD_VENDOR_NAME_REQUIRED_MESSAGE = 'Vendor name is required'
export const LEAD_VENDOR_NAME_PATTERN_MESSAGE = 'Enter a valid vendor name'
export const LEAD_VENDOR_PRODUCTS_REQUIRED_MESSAGE = 'Select at least one product'
export const LEAD_VENDOR_AMOUNT_REQUIRED_MESSAGE = 'Amount is required'

export const MANDATORY_S2_FIELDS: readonly string[] = [
    'kdmContactIdS2',
    'kdmNameS2',
    'kdmDesignationS2',
    'kdmEmailS2',
    'kdmMobileS2',
    'totalStudents',
    'annualFee',
    'tuitionFee',
]

/** S2 fee controls (each capped at 50% of annual fee). Keep in sync with `createLeadInformationForm()`. */
export const S2_OTHER_FEE_FIELDS: readonly string[] = [
    'tuitionFee',
    'developmentFee',
    'smartClassFee',
    'monthlyFees',
    'technicalFee',
]

/** Keep in sync with `updateDemoFeedbackValidators` for EDemoStatus.COMPLETED (date, time, participants, discussion notes). */
export const S3_REQUIRED_FIELDS_COMPLETED: readonly string[] = [
    'demoDateS3',
    'demoTimeS3',
    'demoParticipantIds',
    's3Notes',
]

export const S3_REQUIRED_FIELDS_RESCHEDULED: readonly string[] = ['demoDateS3', 'demoTimeS3']

/**
 * Stage order array for determining read-only status
 * Stages are ordered from S1 to S4
 */
export const LEAD_STAGE_ORDER: readonly string[] = [
    ELeadStageTitle.S1,
    ELeadStageTitle.S2,
    ELeadStageTitle.S3,
    ELeadStageTitle.S4,
] as const

/**
 * Map stage codes to stage IDs for display logic
 */
export const STAGE_CODE_TO_ID: Readonly<Record<string, number>> = {
    S1: 1,
    S2: 2,
    S3: 3,
    S4: 4,
} as const

export const FORM_CONTROL_NAMES = {
    REASON: 'reason',
    EXPECTED_FOLLOWUP_DATE: 'expected_followup_date',
} as const

export const LEAD_STATUS_VALUES = {
    LEAD_ACTIVE: 'Lead Active',
    LEAD_LOST: 'Lead Lost',
    LEAD_DEFERRED: 'Lead Deferred',
} as const

export enum EApiLeadStatus {
    OPEN = 'open',
    ON_HOLD = 'on_hold',
    DROPPED = 'dropped',
}

export enum ELeadStatusModalType {
    LOST = 'lost',
    DEFERRED = 'deferred',
}

export const DEFAULT_VALUES = {
    MIN_REASON_LENGTH: 60,
    MAX_REASON_LENGTH: 500,
} as const

export const ERROR_MESSAGES = {
    REASON_REQUIRED: 'Reason is required',
    UNEXPECTED_ERROR: 'An unexpected error occurred. Please try again.',
} as const

export const UI_LABELS = {
    REASON: 'Reason',
    EXPECTED_FOLLOWUP_DATE: 'Expected Follow-up Date',
    CANCEL: 'Cancel',
    UPDATE_STATUS: 'Update Status',
    UPDATING: 'Updating...',
} as const

export const DIALOG_MESSAGES = {
    REACTIVATE_LEAD: {
        TITLE: 'Reactivate Lead',
        MESSAGE: 'Are you sure you want to reactivate this lead from Deferred to Active?',
        CONFIRM_TEXT: 'Reactivate',
        CANCEL_TEXT: 'Cancel',
    },
    LEAD_STATUS_SUCCESS: {
        TITLE: 'Success',
        LEAD_ACTIVE_MESSAGE: 'The lead has been marked as active.',
        LEAD_LOST_MESSAGE: 'The lead has been marked as lost.',
        LEAD_DEFERRED_MESSAGE: 'The lead has been marked as deferred.',
    },
} as const

/** Shared close icon for CRM modals (Record Communication, Lead Lost/Deferred, client dashboard). */
export const CRM_MODAL_CLOSE_ICON = 'close' as const
